import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api } from "@/lib/api";
import { StatusChip, ModeIcon, formatDateShort, EmptyState } from "@/components/ventrix/atoms";
import Pagination from "@/components/ventrix/Pagination";
import { Search, Plus, Filter, Package } from "lucide-react";

const STATUSES = ["", "AWAITING_APPROVAL", "BOOKED", "PICKED_UP", "IN_TRANSIT", "AT_HUB", "CUSTOMS", "OUT_FOR_DELIVERY", "DELIVERED", "EXCEPTION"];
const MODES = ["", "air", "sea", "road"];
const LIMIT = 50;

export default function ShipmentsList({ section = "customer" }) {
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");   // committed search term
  const [status, setStatus] = useState("");
  const [mode, setMode] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);

  const load = () => {
    setData(null);
    const p = new URLSearchParams();
    if (applied) p.append("q", applied);
    if (status) p.append("status", status);
    if (mode) p.append("mode", mode);
    p.append("limit", String(LIMIT));
    p.append("offset", String(offset));
    api.get(`/v1/shipments?${p.toString()}`).then(({data}) => setData(data)).catch(() => setData({ items: [], total: 0 }));
  };
  useEffect(() => { load(); }, [applied, status, mode, offset]); // eslint-disable-line

  // Any filter/search change returns to the first page.
  const applySearch = () => { setApplied(q); setOffset(0); };
  const setStatusFilter = (v) => { setStatus(v); setOffset(0); };
  const setModeFilter = (v) => { setMode(v); setOffset(0); };

  return (
    <PortalLayout section={section}>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="vtx-eyebrow">/ Shipments</div>
          <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">
            {section === "ops" ? "All shipments" : "Your shipments"}
          </h1>
        </div>
        {section === "ops" && (
          <Link to="/ops/book" className="vtx-btn-danger inline-flex items-center gap-2" data-testid="new-shipment-btn">
            <Plus size={16} /> New booking
          </Link>
        )}
      </div>

      <div className="vtx-card">
        <div className="p-4 border-b border-[#A9C0E0]/40 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input
              className="vtx-input pl-9"
              placeholder="Search tracking, booking ref, origin or destination"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              onKeyDown={(e)=>e.key === "Enter" && applySearch()}
              data-testid="ship-search"
            />
          </div>
          <select value={status} onChange={(e)=>setStatusFilter(e.target.value)} className="vtx-input max-w-[180px]" data-testid="ship-status-filter">
            {STATUSES.map(s => <option key={s} value={s}>{s ? s.replace(/_/g," ") : "All statuses"}</option>)}
          </select>
          <select value={mode} onChange={(e)=>setModeFilter(e.target.value)} className="vtx-input max-w-[140px]" data-testid="ship-mode-filter">
            {MODES.map(m => <option key={m} value={m}>{m ? m.toUpperCase() : "All modes"}</option>)}
          </select>
          <button className="vtx-btn-ghost inline-flex items-center gap-2" onClick={applySearch} data-testid="ship-apply-filters"><Filter size={14}/> Apply</button>
        </div>

        {data === null && <div className="p-6 space-y-2">{[1,2,3,4,5].map(i=><div key={i} className="vtx-skeleton h-12" />)}</div>}
        {data && (data.items || []).length === 0 && (
          <EmptyState
            icon={Package}
            title="No shipments match"
            hint={section === "ops" ? "Adjust your filters, or create a new booking." : "Adjust your filters to find your shipments."}
          >
            {section === "ops" && (
              <Link to="/ops/book" className="vtx-btn-primary inline-flex items-center gap-2"><Plus size={14} /> New booking</Link>
            )}
          </EmptyState>
        )}
        {data && (data.items || []).length > 0 && (
          <div className="overflow-x-auto">
            <table className="vtx-table w-full">
              <thead>
                <tr>
                  <th>Tracking</th><th>Booking ref</th><th>Mode</th><th>Route</th>
                  <th>Status</th><th>ETA</th><th>Weight</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.id} data-testid={`ship-row-${s.tracking_number}`}>
                    <td>
                      <Link to={section === "ops" ? `/ops/shipments/${s.id}` : `/app/shipments/${s.id}`}
                            className="font-mono text-[#0E2F76] font-semibold hover:text-[#d41f26]">
                        {s.tracking_number}
                      </Link>
                    </td>
                    <td className="font-mono text-xs">{s.booking_ref}</td>
                    <td><ModeIcon mode={s.mode} showLabel /></td>
                    <td className="text-[#475569] text-xs">{s.origin} → {s.destination}</td>
                    <td><StatusChip status={s.status} /></td>
                    <td className="font-mono text-[#0E2F76]">{formatDateShort(s.eta)}</td>
                    <td className="font-mono">{s.total_weight_kg} kg</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pagination total={data.total ?? (data.items || []).length} limit={LIMIT} offset={offset} onChange={setOffset} />}
      </div>
    </PortalLayout>
  );
}
